import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, fromEvent, merge } from "rxjs";
import { map, filter } from "rxjs/operators";

export interface PWAInstallPrompt {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface PWAUpdate {
  available: boolean;
  activate: () => Promise<void>;
}

export interface PWANotification {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
}

@Injectable({
  providedIn: "root",
})
export class PwaService {
  private installPromptSubject = new BehaviorSubject<PWAInstallPrompt | null>(null);
  private updateSubject = new BehaviorSubject<PWAUpdate | null>(null);
  private onlineSubject = new BehaviorSubject<boolean>(navigator.onLine);
  private notificationPermissionSubject = new BehaviorSubject<NotificationPermission>("default");

  constructor() {
    this.initializePWA();
    this.setupEventListeners();
  }

  /**
   * Inicializar PWA
   */
  private initializePWA(): void {
    // Registrar Service Worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/service-worker.js")
        .then((registration) => {
          this.checkForUpdates(registration);
        })
        .catch((error) => {});
    }

    // Verificar permissão de notificações
    this.updateNotificationPermission();
  }

  /**
   * Configurar event listeners
   */
  private setupEventListeners(): void {
    // Listener para prompt de instalação
    window.addEventListener("beforeinstallprompt", (event) => {
      event.preventDefault();
      this.installPromptSubject.next(event as any);
    });

    // Listener para atualizações do Service Worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        window.location.reload();
      });
    }

    // Listener para status online/offline
    merge(fromEvent(window, "online"), fromEvent(window, "offline")).subscribe(() => {
      this.onlineSubject.next(navigator.onLine);
    });

    // Listener para mudanças de permissão de notificação
    if ("Notification" in window) {
      this.updateNotificationPermission();
    }
  }

  /**
   * Verificar atualizações do Service Worker
   */
  private checkForUpdates(registration: ServiceWorkerRegistration): void {
    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            this.updateSubject.next({
              available: true,
              activate: () => {
                newWorker.postMessage({ type: "SKIP_WAITING" });
                return new Promise((resolve) => {
                  newWorker.addEventListener("statechange", () => {
                    if (newWorker.state === "activated") {
                      resolve();
                    }
                  });
                });
              },
            });
          }
        });
      }
    });
  }

  /**
   * Obter observable do prompt de instalação
   */
  getInstallPrompt(): Observable<PWAInstallPrompt | null> {
    return this.installPromptSubject.asObservable();
  }

  /**
   * Obter observable de atualizações
   */
  getUpdates(): Observable<PWAUpdate | null> {
    return this.updateSubject.asObservable();
  }

  /**
   * Obter observable do status online/offline
   */
  getOnlineStatus(): Observable<boolean> {
    return this.onlineSubject.asObservable();
  }

  /**
   * Obter observable da permissão de notificações
   */
  getNotificationPermission(): Observable<NotificationPermission> {
    return this.notificationPermissionSubject.asObservable();
  }

  /**
   * Instalar PWA
   */
  async installPWA(): Promise<boolean> {
    const prompt = this.installPromptSubject.value;
    if (!prompt) {
      return false;
    }

    try {
      await prompt.prompt();
      const choiceResult = await prompt.userChoice;

      if (choiceResult.outcome === "accepted") {
        this.installPromptSubject.next(null);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  /**
   * Verificar se PWA pode ser instalado
   */
  canInstall(): boolean {
    return this.installPromptSubject.value !== null;
  }

  /**
   * Verificar se PWA está instalado
   */
  isInstalled(): boolean {
    return window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone === true;
  }

  /**
   * Solicitar permissão para notificações
   */
  async requestNotificationPermission(): Promise<boolean> {
    if (!("Notification" in window)) {
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      this.updateNotificationPermission();
      return permission === "granted";
    } catch (error) {
      return false;
    }
  }

  /**
   * Enviar notificação
   */
  async sendNotification(notification: PWANotification): Promise<boolean> {
    if (!("Notification" in window) || Notification.permission !== "granted") {
      return false;
    }

    try {
      const notificationOptions: NotificationOptions = {
        body: notification.body,
        icon: notification.icon || "/assets/icons/icon-192x192.png",
        badge: notification.badge || "/assets/icons/badge-72x72.png",
        tag: notification.tag,
        data: notification.data,
        actions: notification.actions,
        requireInteraction: true,
        silent: false,
      };

      const notif = new Notification(notification.title, notificationOptions);

      notif.onclick = () => {
        window.focus();
        notif.close();
      };

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Atualizar permissão de notificação
   */
  private updateNotificationPermission(): void {
    if ("Notification" in window) {
      this.notificationPermissionSubject.next(Notification.permission);
    }
  }

  /**
   * Verificar se está online
   */
  isOnline(): boolean {
    return navigator.onLine;
  }

  /**
   * Verificar se PWA está suportado
   */
  isSupported(): boolean {
    return "serviceWorker" in navigator && "PushManager" in window;
  }

  /**
   * Obter informações do PWA
   */
  getPWAInfo(): any {
    return {
      isInstalled: this.isInstalled(),
      canInstall: this.canInstall(),
      isOnline: this.isOnline(),
      isSupported: this.isSupported(),
      notificationPermission: Notification.permission,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
    };
  }

  /**
   * Limpar cache do PWA
   */
  async clearCache(): Promise<boolean> {
    if (!("caches" in window)) {
      return false;
    }

    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Obter tamanho do cache
   */
  async getCacheSize(): Promise<number> {
    if (!("caches" in window)) {
      return 0;
    }

    try {
      const cacheNames = await caches.keys();
      let totalSize = 0;

      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const requests = await cache.keys();

        for (const request of requests) {
          const response = await cache.match(request);
          if (response) {
            const blob = await response.blob();
            totalSize += blob.size;
          }
        }
      }

      return totalSize;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Sincronizar dados offline
   */
  async syncOfflineData(): Promise<boolean> {
    if (!("serviceWorker" in navigator)) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      if ("sync" in registration) {
        await (registration as any).sync.register("background-sync");
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Obter estatísticas do PWA
   */
  async getPWAStats(): Promise<any> {
    const cacheSize = await this.getCacheSize();
    const pwaInfo = this.getPWAInfo();

    return {
      ...pwaInfo,
      cacheSize,
      cacheSizeFormatted: this.formatBytes(cacheSize),
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Formatar bytes para formato legível
   */
  private formatBytes(bytes: number): string {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }
}
